import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, History, Loader2, Pencil, RefreshCw, Save, Undo2 } from "lucide-react";
import { useACPP } from "../ACPPContext";
import AdminAPI from "../../../services/AdminAPI";
import PlanEditor, { mkId } from "./module6/editor/PlanEditor";
import ClassPicker from "./module6/ClassPicker";
import CopyDrawer from "./module6/CopyDrawer";
import ActivityPanel from "./module6/ActivityPanel";
import PlanStatusCard from "./module6/PlanStatusCard";
import { Toast, ConfirmDialog } from "./module6/feedback";
import { SectionCard } from "./module6/editor/Cards";
import { displayStatusOf, STATUS_META } from "./module6/workflowMeta";

const EMPTY_OPTIONS = [];
const STATUS_FILTERS = [
  { key: "", label: "All statuses" },
  { key: "hod_review", label: STATUS_META.hod_review.label },
  { key: "admin_review", label: STATUS_META.admin_review.label },
  { key: "changes_requested", label: STATUS_META.changes_requested.label },
  { key: "approved", label: STATUS_META.approved.label },
  { key: "teacher_draft", label: STATUS_META.teacher_draft.label },
];
const GRADES = ["BV1", "BV2", "BV3", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

function cloneTermsWithNewIds(terms) {
  if (!Array.isArray(terms)) return [];
  return terms.map((term) => ({
    ...term,
    units: (term.units || []).map((unit) => ({
      ...unit,
      id: mkId("unit"),
      chapters: (unit.chapters || []).map((ch) => ({
        ...ch,
        id: mkId("ch"),
        topics: (ch.topics || []).map((t) => ({ ...t, id: mkId("topic"), subtopics: [...(t.subtopics || [])] })),
        pedagogies: [...(ch.pedagogies || [])],
        customPedagogies: [...(ch.customPedagogies || [])],
        learningObjectives: [...(ch.learningObjectives || [])],
        competencies: [...(ch.competencies || [])],
        customCompetencies: [...(ch.customCompetencies || [])],
      })),
    })),
  }));
}

const now = () => new Date().toISOString();

/**
 * Module 6 on the admin side: browse every class's plan and, where your role
 * allows, correct its content. Approving or sending back happens in Plan
 * Approvals, which has the queue, diff, history and bulk actions.
 */
export default function Module6TeacherWorkflow() {
  const navigate = useNavigate();
  const {
    module5OwnershipMappings, module5Loading,
    module6Plans, setModule6Plans,
    module6Loading, module6LoadError, module6Saving, module6SaveError,
    saveModule6TeacherWorkflow, loadModule6TeacherWorkflow,
    currentUserRole, canEditModule6,
    getActiveAcademicYear, createEmptyM6Plan,
  } = useACPP();

  const isAdminRole = ["admin", "management"].includes(currentUserRole);
  const isHodRole = currentUserRole === "hod";

  const [selectedUid, setSelectedUid] = useState("");
  const [query, setQuery] = useState("");
  const [gradeFilter, setGradeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null); // "edit-approved" | "discard"
  const [editingApprovedUid, setEditingApprovedUid] = useState("");
  const [dirty, setDirty] = useState(false);
  const [competencyOptions, setCompetencyOptions] = useState(EMPTY_OPTIONS);
  const [copy, setCopy] = useState({ open: false, loading: false, error: "", plans: { same_teacher: [], other_teachers: [] } });
  const [importNote, setImportNote] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const plansRef = useRef(module6Plans);
  plansRef.current = module6Plans;
  const notify = useCallback((type, msg) => setToast({ type, msg }), []);

  const baseRows = useMemo(
    () => module5OwnershipMappings.filter((r) => r.grade && r.section && r.subject),
    [module5OwnershipMappings],
  );
  const visibleRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return baseRows.filter((r) => {
      if (gradeFilter && r.grade !== gradeFilter) return false;
      if (statusFilter && displayStatusOf(module6Plans[r.uid]) !== statusFilter) return false;
      if (!q) return true;
      return `${r.grade} ${r.section} ${r.subject} ${r.teacher_name || ""}`.toLowerCase().includes(q);
    });
  }, [baseRows, query, gradeFilter, statusFilter, module6Plans]);

  const selectedBase = useMemo(() => baseRows.find((r) => r.uid === selectedUid) || null, [baseRows, selectedUid]);
  const selectedPlan = selectedBase ? module6Plans[selectedUid] || null : null;
  const stage = selectedPlan?.workflow_stage || selectedPlan?.workflowStage || "teacher_draft";
  const displayStatus = displayStatusOf(selectedPlan);
  const editingApproved = stage === "approved" && editingApprovedUid === selectedUid;

  // Who may change content at which stage (the server enforces the same rules).
  const canEditNow =
    !!selectedPlan && canEditModule6 && (
      (isAdminRole && (stage !== "approved" || editingApproved)) ||
      (isHodRole && stage === "hod_review")
    );
  const canStartEditApproved = !!selectedPlan && isAdminRole && stage === "approved" && !editingApproved;
  const locked = !canEditNow;

  // Summary counts for the header line.
  const counts = useMemo(() => {
    const c = { hod_review: 0, admin_review: 0, changes_requested: 0, approved: 0 };
    baseRows.forEach((r) => {
      const s = displayStatusOf(module6Plans[r.uid]);
      if (s in c) c[s] += 1;
    });
    return c;
  }, [baseRows, module6Plans]);

  useEffect(() => {
    let cancelled = false;
    AdminAPI.acppGetReferenceCompetencies(selectedBase?.grade || "", selectedBase?.subject || "")
      .then((res) => !cancelled && setCompetencyOptions(Array.isArray(res?.competencies) ? res.competencies.filter(Boolean) : EMPTY_OPTIONS))
      .catch(() => !cancelled && setCompetencyOptions(EMPTY_OPTIONS));
    return () => { cancelled = true; };
  }, [selectedBase?.grade, selectedBase?.subject]);

  const select = (uid) => {
    setSelectedUid(uid);
    setEditingApprovedUid("");
    setImportNote("");
    const base = baseRows.find((r) => r.uid === uid);
    if (base && !module6Plans[uid]) setModule6Plans((prev) => ({ ...prev, [uid]: createEmptyM6Plan(base) }));
  };

  const applyPlanChange = useCallback((mutate) => {
    if (!selectedUid) return;
    setModule6Plans((prev) => {
      const plan = prev[selectedUid];
      if (!plan) return prev;
      const draft = JSON.parse(JSON.stringify(plan));
      mutate(draft);
      draft.lastUpdated = now();
      return { ...prev, [selectedUid]: draft };
    });
    setDirty(true);
  }, [selectedUid, setModule6Plans]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // Only the plan being edited is sent, so a save can never disturb other rows.
  const save = async () => {
    const plan = plansRef.current[selectedUid];
    if (!plan) return;
    try {
      await saveModule6TeacherWorkflow({ [selectedUid]: plan });
      setDirty(false);
      if (editingApproved) {
        setEditingApprovedUid("");
        await loadModule6TeacherWorkflow().catch(() => {});
        notify("success", "Saved. The plan is back in HOD review.");
      } else {
        notify("success", "Changes saved.");
      }
    } catch (err) {
      notify("error", err?.message || "Could not save your changes.");
    }
  };

  const discard = async () => {
    setConfirm(null);
    setEditingApprovedUid("");
    setDirty(false);
    await loadModule6TeacherWorkflow().catch(() => {});
  };

  const openCopy = async () => {
    if (!selectedUid) return;
    setCopy((c) => ({ ...c, open: true, loading: true, error: "" }));
    try {
      const res = await AdminAPI.acppGetModule6CopyablePlans(getActiveAcademicYear(), selectedUid);
      setCopy((c) => ({ ...c, loading: false, plans: { same_teacher: res?.same_teacher || [], other_teachers: res?.other_teachers || [] } }));
    } catch (e) {
      setCopy((c) => ({ ...c, loading: false, error: e?.message || "Failed to load plans." }));
    }
  };

  const importPlan = (item) => {
    const terms = cloneTermsWithNewIds(item.terms);
    setModule6Plans((prev) => {
      const plan = prev[selectedUid];
      return plan ? { ...prev, [selectedUid]: { ...plan, terms, lastUpdated: now() } } : prev;
    });
    setImportNote(`Imported from ${item.grade}-${item.section} ${item.subject}. Review it, then save.`);
    setCopy((c) => ({ ...c, open: false }));
    setDirty(true);
  };

  const loadError = module6LoadError || module6SaveError;
  const thread = Array.isArray(selectedPlan?.remarksThread) ? selectedPlan.remarksThread : [];

  return (
    <div className="space-y-6 font-[poppins]">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <span className="text-sm text-gray-500">
          {baseRows.length} classes · {counts.hod_review} with HOD · {counts.admin_review} with admin · {counts.changes_requested} need changes · {counts.approved} approved
        </span>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate("/acpp/approvals")}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[#1565c0] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0f4f99]"
          >
            <ClipboardCheck className="h-4 w-4" /> Workflow Approvals
          </button>
          <button
            type="button"
            onClick={() => loadModule6TeacherWorkflow().then(() => setDirty(false)).catch(() => {})}
            disabled={module6Loading || module6Saving}
            className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${module6Loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </header>

      {loadError && <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-[13px] text-rose-800" role="alert">{loadError}</div>}

      {!module5Loading && !baseRows.length ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center text-[14px] text-slate-500">
          No classes are mapped yet. Set up the ownership mapping (Module 5) first.
        </div>
      ) : (
        <>
          <ClassPicker
            rows={visibleRows}
            plans={module6Plans}
            selectedUid={selectedUid}
            onSelect={select}
            query={query}
            onQuery={setQuery}
            count={baseRows.length}
            extra={
              <div className="mt-2 grid grid-cols-2 gap-2">
                <select value={gradeFilter} onChange={(e) => setGradeFilter(e.target.value)} aria-label="Filter by grade" className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[12px]">
                  <option value="">All grades</option>
                  {GRADES.map((g) => <option key={g} value={g}>Grade {g}</option>)}
                </select>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Filter by status" className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-[12px]">
                  {STATUS_FILTERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              </div>
            }
          />

          {!selectedPlan ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center text-[14px] text-slate-500">
              {module6Loading ? "Loading plans…" : "Choose a class above to view its plan."}
            </div>
          ) : (
            <>
              <PlanStatusCard
                plan={selectedPlan}
                displayStatus={displayStatus}
                teacherName={selectedBase?.teacher_name}
                hodName={selectedBase?.hod_name}
                note={!canEditNow && !canStartEditApproved ? (isHodRole && stage !== "hod_review" ? "You can edit a plan while it is in HOD review." : "Read-only at this stage.") : ""}
              />

              {importNote && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
                  <span className="flex-1">{importNote}</span>
                  <button type="button" onClick={() => setImportNote("")} className="text-[12px] font-semibold hover:underline">Dismiss</button>
                </div>
              )}

              <PlanEditor
                key={selectedUid}
                plan={selectedPlan}
                locked={locked}
                onPlanChange={applyPlanChange}
                competencyOptions={competencyOptions}
                onCopy={canEditNow ? openCopy : undefined}
              />

              {(thread.length > 0 || (selectedPlan.status_history || []).length > 0) && (
                <SectionCard
                  icon={History}
                  title="Feedback and history"
                  subtitle={thread.length ? `${thread.length} message${thread.length === 1 ? "" : "s"}` : "Everything that happened to this plan"}
                  open={historyOpen}
                  onToggle={() => setHistoryOpen((v) => !v)}
                >
                  <ActivityPanel thread={thread} history={selectedPlan.status_history || []} />
                </SectionCard>
              )}

              {(canEditNow || canStartEditApproved) && (
                <div className="sticky bottom-3 z-20 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
                  <span className={`text-[12px] ${dirty ? "text-amber-600" : "text-slate-500"}`}>
                    {dirty
                      ? "Unsaved changes"
                      : editingApproved
                      ? "Editing an approved plan: saving sends it back to HOD review."
                      : canStartEditApproved
                      ? "Approved. Editing it sends it back to HOD review."
                      : "No unsaved changes"}
                  </span>
                  <div className="flex gap-2">
                    {canStartEditApproved && (
                      <button type="button" onClick={() => setConfirm("edit-approved")} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50">
                        <Pencil className="h-4 w-4" /> Edit approved plan
                      </button>
                    )}
                    {editingApproved && (
                      <button type="button" onClick={() => (dirty ? setConfirm("discard") : discard())} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-[13px] font-semibold text-slate-700 hover:bg-slate-50">
                        <Undo2 className="h-4 w-4" /> Discard
                      </button>
                    )}
                    {canEditNow && (
                      <button type="button" onClick={save} disabled={module6Saving || !dirty} className="inline-flex items-center gap-1.5 rounded-lg bg-[#1565c0] px-4 py-2 text-[13px] font-semibold text-white hover:bg-[#0f4f99] disabled:opacity-50">
                        {module6Saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        {editingApproved ? "Save and send to HOD" : "Save changes"}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}


      <CopyDrawer open={copy.open} loading={copy.loading} error={copy.error} plans={copy.plans} onClose={() => setCopy((c) => ({ ...c, open: false }))} onImport={importPlan} />
      <ConfirmDialog
        open={confirm === "edit-approved"}
        title="Edit an approved plan?"
        body="Saving a content change sends this plan back to HOD review, and then the admin, for approval again."
        confirmLabel="Start editing"
        onCancel={() => setConfirm(null)}
        onConfirm={() => { setEditingApprovedUid(selectedUid); setConfirm(null); }}
      />
      <ConfirmDialog
        open={confirm === "discard"}
        title="Discard your changes?"
        body="Your edits have not been saved and will be lost."
        confirmLabel="Discard"
        tone="danger"
        onCancel={() => setConfirm(null)}
        onConfirm={discard}
      />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
